import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["generateBtn", "auditBtn", "suggestions", "auditResults", "loading"]
  static values = { pageId: String }

  async generateMetadata() {
    this.showLoading()
    this.hideSuggestions()
    this.hideAuditResults()

    try {
      const response = await fetch('/admin/pages/ai/generate_metadata', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ page_id: this.pageIdValue })
      })

      const data = await response.json()

      if (data.success) {
        this.displaySuggestions(data.suggestions)
      } else {
        this.showError(data.error || "Failed to generate metadata")
      }
    } catch (error) {
      console.error('AI Metadata Generation Error:', error)
      this.showError("Failed to generate metadata: " + error.message)
    } finally {
      this.hideLoading()
    }
  }

  async runAudit() {
    this.showLoading()
    this.hideSuggestions()
    this.hideAuditResults()

    try {
      const response = await fetch('/admin/pages/ai/seo_audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ page_id: this.pageIdValue })
      })

      const data = await response.json()

      if (data.success) {
        this.displayAuditResults(data.audit)
      } else {
        this.showError(data.error || "Failed to run SEO audit")
      }
    } catch (error) {
      console.error('AI SEO Audit Error:', error)
      this.showError("Failed to run SEO audit: " + error.message)
    } finally {
      this.hideLoading()
    }
  }

  displaySuggestions(suggestions) {
    if (!this.hasSuggestionsTarget) return

    const html = `
      <div class="bg-white dark:bg-neutral-800 rounded-lg p-4 space-y-4">
        <h3 class="font-semibold text-gray-900 dark:text-white">
          <i class="fas fa-magic text-purple-600 mr-2"></i>AI Suggestions
        </h3>

        ${suggestions.meta_title ? `
          <div class="border-l-4 border-purple-500 pl-4">
            <div class="text-xs text-gray-600 dark:text-neutral-400 mb-1">Suggested Meta Title:</div>
            <div class="text-sm text-gray-900 dark:text-white mb-2">${this.escapeHtml(suggestions.meta_title)}</div>
            <button type="button"
              data-action="click->ai-metadata#applySuggestion"
              data-field="meta_title"
              data-value="${this.escapeHtml(suggestions.meta_title)}"
              class="text-xs px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded">
              Apply Title
            </button>
          </div>
        ` : ''}

        ${suggestions.meta_description ? `
          <div class="border-l-4 border-blue-500 pl-4">
            <div class="text-xs text-gray-600 dark:text-neutral-400 mb-1">Suggested Meta Description:</div>
            <div class="text-sm text-gray-900 dark:text-white mb-2">${this.escapeHtml(suggestions.meta_description)}</div>
            <button type="button"
              data-action="click->ai-metadata#applySuggestion"
              data-field="meta_description"
              data-value="${this.escapeHtml(suggestions.meta_description)}"
              class="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">
              Apply Description
            </button>
          </div>
        ` : ''}

        ${suggestions.keywords ? `
          <div class="border-l-4 border-green-500 pl-4">
            <div class="text-xs text-gray-600 dark:text-neutral-400 mb-1">Suggested Keywords:</div>
            <div class="text-sm text-gray-900 dark:text-white mb-2">${this.escapeHtml(suggestions.keywords)}</div>
            <button type="button"
              data-action="click->ai-metadata#applySuggestion"
              data-field="keywords"
              data-value="${this.escapeHtml(suggestions.keywords)}"
              class="text-xs px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded">
              Apply Keywords
            </button>
          </div>
        ` : ''}

        <button type="button"
          data-action="click->ai-metadata#applyAllSuggestions"
          data-suggestions='${JSON.stringify(suggestions)}'
          class="w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold">
          <i class="fas fa-check-double mr-2"></i>Apply All Suggestions
        </button>
      </div>
    `

    this.suggestionsTarget.innerHTML = html
    this.suggestionsTarget.classList.remove('hidden')
  }

  displayAuditResults(audit) {
    if (!this.hasAuditResultsTarget) return

    const scoreColor = audit.score >= 90 ? 'green' : audit.score >= 70 ? 'blue' : audit.score >= 50 ? 'yellow' : 'red'

    const html = `
      <div class="bg-white dark:bg-neutral-800 rounded-lg p-4 space-y-4">
        <h3 class="font-semibold text-gray-900 dark:text-white">
          <i class="fas fa-chart-line text-blue-600 mr-2"></i>SEO Audit Results
        </h3>

        <div class="text-center py-4 bg-gray-50 dark:bg-neutral-900 rounded-lg">
          <div class="text-5xl font-bold text-${scoreColor}-600">${audit.score}</div>
          <div class="text-sm text-gray-600 dark:text-neutral-400 mt-1">SEO Score</div>
        </div>

        ${audit.issues && audit.issues.length > 0 ? `
          <div>
            <h4 class="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
              <i class="fas fa-exclamation-triangle mr-1"></i>Issues Found:
            </h4>
            <ul class="list-disc list-inside text-sm text-gray-700 dark:text-neutral-300 space-y-1">
              ${audit.issues.map(issue => `<li>${this.escapeHtml(issue)}</li>`).join('')}
            </ul>
          </div>
        ` : ''}

        ${audit.recommendations && audit.recommendations.length > 0 ? `
          <div>
            <h4 class="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">
              <i class="fas fa-lightbulb mr-1"></i>Recommendations:
            </h4>
            <ul class="list-disc list-inside text-sm text-gray-700 dark:text-neutral-300 space-y-1">
              ${audit.recommendations.map(rec => `<li>${this.escapeHtml(rec)}</li>`).join('')}
            </ul>
          </div>
        ` : ''}

        ${audit.readability ? `
          <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <div class="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-1">Readability:</div>
            <div class="text-sm text-blue-900 dark:text-blue-400">${this.escapeHtml(audit.readability)}</div>
          </div>
        ` : ''}
      </div>
    `

    this.auditResultsTarget.innerHTML = html
    this.auditResultsTarget.classList.remove('hidden')
  }

  applySuggestion(event) {
    const field = event.currentTarget.dataset.field
    const value = event.currentTarget.dataset.value

    const input = document.querySelector(`[name="page[${field}]"]`)
    if (input) {
      input.value = value

      // Trigger input event for SERP preview update
      input.dispatchEvent(new Event('input', { bubbles: true }))

      // Visual feedback
      event.currentTarget.textContent = '✓ Applied'
      event.currentTarget.classList.add('opacity-50')
      event.currentTarget.disabled = true
    }
  }

  applyAllSuggestions(event) {
    const suggestions = JSON.parse(event.currentTarget.dataset.suggestions)

    Object.entries(suggestions).forEach(([field, value]) => {
      const input = document.querySelector(`[name="page[${field}]"]`)
      if (input && value) {
        input.value = value
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
    })

    // Visual feedback
    event.currentTarget.innerHTML = '<i class="fas fa-check mr-2"></i>All Applied!'
    event.currentTarget.classList.add('opacity-50')
    event.currentTarget.disabled = true
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  hideSuggestions() {
    if (this.hasSuggestionsTarget) {
      this.suggestionsTarget.classList.add('hidden')
    }
  }

  hideAuditResults() {
    if (this.hasAuditResultsTarget) {
      this.auditResultsTarget.classList.add('hidden')
    }
  }

  showError(message) {
    alert('Error: ' + message)
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
